/**
 * The log-report kit's pure half (website roadmap S09; every later log section — energy, travel, attacks, markets —
 * reuses it): date ranges (presets, the page address's ?from=&to=, a chart's zoom), Torn days (TCT = UTC), per-day
 * series, which stretches of the log have been read in full, and CSV. Browser-safe; tested in
 * packages/shared/test/log-kit.test.ts. The browser half (IndexedDB, the picker, the zoomable chart) is apps/web/src/app/log-kit/.
 */

const DAY = 86_400_000;

/** A stretch of time (ms; null = open on that side). */
export interface DateRange {
  from: number | null;
  to: number | null;
}

export const RANGE_PRESETS = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '1y', label: '1 year', days: 365 },
  { id: 'all', label: 'All', days: null },
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number]['id'];

/** Start of the Torn day (UTC midnight) a moment falls in. */
export const dayStart = (ms: number): number => Math.floor(ms / DAY) * DAY;

/** "2026-10-30" for a moment (Torn's day). */
export const dayKey = (ms: number): string => new Date(dayStart(ms)).toISOString().slice(0, 10);

/** "2026-10-30" → that day's start (ms), null if it isn't a date. */
export function parseDay(s: string | null | undefined): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s ?? '').trim());
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) && dayKey(t) === m[0] ? t : null;
}

/** A preset's range ending now: the last N Torn days, today included. */
export function presetRange(id: RangePreset, now: number): DateRange {
  const p = RANGE_PRESETS.find((x) => x.id === id);
  if (!p || p.days === null) return { from: null, to: null };
  return { from: dayStart(now) - (p.days - 1) * DAY, to: null };
}

/** Which preset a range is (null: a custom one). */
export function presetOf(r: DateRange, now: number): RangePreset | null {
  for (const p of RANGE_PRESETS) {
    const x = presetRange(p.id, now);
    if (x.from === r.from && x.to === r.to) return p.id;
  }
  return null;
}

/**
 * The page address's range: ?range=30d, or ?from=YYYY-MM-DD&to=YYYY-MM-DD (to = that whole day). Nothing given → the
 * fallback preset. A "to" before the "from" is swapped.
 */
export function rangeFromQuery(search: string, now: number, fallback: RangePreset = '30d'): DateRange {
  const q = new URLSearchParams(search);
  const preset = RANGE_PRESETS.find((p) => p.id === q.get('range'));
  if (preset) return presetRange(preset.id, now);
  let from = parseDay(q.get('from'));
  let to = parseDay(q.get('to'));
  if (from === null && to === null) return presetRange(fallback, now);
  if (from !== null && to !== null && to < from) [from, to] = [to, from];
  return { from, to: to === null ? null : to + DAY - 1 };
}

/** The range for the page address (merged into the rest of its query): a preset by name, else from/to days. */
export function rangeToQuery(r: DateRange, now: number, search = ''): string {
  const q = new URLSearchParams(search);
  for (const k of ['range', 'from', 'to']) q.delete(k);
  const p = presetOf(r, now);
  if (p) q.set('range', p);
  else {
    if (r.from !== null) q.set('from', dayKey(r.from));
    if (r.to !== null) q.set('to', dayKey(r.to));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const inRange = (ms: number, r: DateRange): boolean => (r.from === null || ms >= r.from) && (r.to === null || ms <= r.to);

/** A range made from a chart's zoom (two moments in any order), whole Torn days. */
export function zoomRange(a: number, b: number): DateRange {
  const [x, y] = a <= b ? [a, b] : [b, a];
  return { from: dayStart(x), to: dayStart(y) + DAY - 1 };
}

/** "1 Oct – 30 Oct 2026" / "Everything kept" / "Since 1 Oct 2026". */
export function rangeText(r: DateRange): string {
  const f = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  if (r.from === null && r.to === null) return 'Everything kept';
  if (r.from === null) return `Up to ${f(r.to!)}`;
  if (r.to === null) return `Since ${f(r.from)}`;
  return `${f(r.from)} – ${f(r.to)}`;
}

/**
 * Values summed per Torn day over a range, with the empty days in between (so a chart's gaps show). An open range runs
 * from the first item's day to the last's (or to `now` when only "from" is open-ended… to now).
 */
export function sumPerDay<T extends { at: number }>(items: readonly T[], value: (x: T) => number, r: DateRange, now: number, maxDays = 1500): Array<{ at: number; v: number }> {
  const inside = items.filter((x) => inRange(x.at, r));
  if (!inside.length && r.from === null) return [];
  const first = r.from ?? Math.min(...inside.map((x) => x.at));
  const last = Math.min(r.to ?? now, now);
  const start = Math.max(dayStart(first), dayStart(last) - (maxDays - 1) * DAY);
  const sums = new Map<number, number>();
  for (const x of inside) {
    const d = dayStart(x.at);
    if (d >= start) sums.set(d, (sums.get(d) ?? 0) + value(x));
  }
  const out: Array<{ at: number; v: number }> = [];
  for (let d = start; d <= dayStart(last); d += DAY) out.push({ at: d, v: sums.get(d) ?? 0 });
  return out;
}

// ---------------------------------------------------------------- what has been read in full

/**
 * A stretch of the log read without a gap (ms, from ≤ to). A reader keeps a list of these per "stream" (the whole log,
 * or one category), so it knows what's left to ask Torn for.
 */
export interface Span {
  from: number;
  to: number;
}

/** Spans joined where they touch or overlap (within `slack` ms), oldest first. */
export function mergeSpans(spans: readonly Span[], slack = 1000): Span[] {
  const s = spans.filter((x) => Number.isFinite(x.from) && Number.isFinite(x.to) && x.from <= x.to).sort((a, b) => a.from - b.from);
  const out: Span[] = [];
  for (const x of s) {
    const last = out[out.length - 1];
    if (last && x.from <= last.to + slack) last.to = Math.max(last.to, x.to);
    else out.push({ ...x });
  }
  return out;
}

/** What every one of several streams has read in full (the stretches all of them cover), oldest first. */
export function intersectSpans(lists: ReadonlyArray<readonly Span[]>): Span[] {
  if (!lists.length) return [];
  let acc = mergeSpans(lists[0]!);
  for (const next of lists.slice(1)) {
    const b = mergeSpans(next);
    const out: Span[] = [];
    for (const x of acc) for (const y of b) {
      const from = Math.max(x.from, y.from);
      const to = Math.min(x.to, y.to);
      if (from <= to) out.push({ from, to });
    }
    acc = mergeSpans(out);
  }
  return acc;
}

/** The newest span (what "read newer" continues from), null if none. */
export const newestSpan = (spans: readonly Span[]): Span | null => mergeSpans(spans).at(-1) ?? null;

/** Where to continue reading older entries: just before the start of the newest unbroken span (null: nothing read). */
export function olderCursor(spans: readonly Span[]): number | null {
  const n = newestSpan(spans);
  return n ? n.from : null;
}

/** How much of a range is covered by spans (0–1), for "read in full" notes. */
export function covered(spans: readonly Span[], r: DateRange, now: number): number {
  const from = r.from ?? Math.min(...spans.map((s) => s.from), now);
  const to = Math.min(r.to ?? now, now);
  if (to <= from) return 1;
  let have = 0;
  for (const s of mergeSpans(spans)) have += Math.max(0, Math.min(s.to, to) - Math.max(s.from, from));
  return Math.min(1, have / (to - from));
}

// ---------------------------------------------------------------- CSV

/** One CSV cell: quoted when needed; text that a spreadsheet would run as a formula gets a leading ' (CSV injection). */
export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  let s = v;
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(header: readonly string[], rows: ReadonlyArray<ReadonlyArray<string | number | null | undefined>>): string {
  return `${[header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n')}\n`;
}

// ---------------------------------------------------------------- Torn's log categories

/** v2 `torn/logcategories` (`logcategories: [{ id, title }]`) → the id of the category whose title matches, else null. */
export function findLogCategory(body: unknown, title: RegExp): number | null {
  const root = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const raw = root.logcategories ?? root.categories;
  const rows = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.entries(raw as Record<string, unknown>).map(([id, v]) => (v && typeof v === 'object' ? { id, ...(v as object) } : { id, title: v })) : [];
  for (const r of rows as Array<Record<string, unknown>>) {
    const id = Number(r?.id);
    if (Number.isInteger(id) && id > 0 && typeof r?.title === 'string' && title.test(r.title.trim())) return id;
  }
  return null;
}
